'use client';

import { useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  FIELD_PLACEHOLDER_CLASS,
  INPUT_PLACEHOLDER_CLASS,
} from './FilterControls';

export function FilterValuePicker({
  label,
  options,
  selected,
  onToggle,
  onRemove,
  single = false,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
  onRemove: (value: string) => void;
  single?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
        >
          <div className="flex flex-wrap gap-1 w-full min-w-0 pr-5">
            {!selected.length && (
              <span className={FIELD_PLACEHOLDER_CLASS}>{label}</span>
            )}
            {selected.map((value) => {
              const text =
                options.find((option) => option.value === value)?.label ??
                value;
              return (
                <Badge
                  key={value}
                  variant="secondary"
                  className="max-w-full text-xs font-normal gap-1 pr-1 border border-foreground"
                >
                  <span className="min-w-0 truncate">{text}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={t('search.activeFilters.remove', {
                      label: text,
                    })}
                    className="inline-flex min-h-11 min-w-11 lg:min-h-4 lg:min-w-4 shrink-0 items-center justify-center cursor-pointer hover:bg-muted rounded-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemove(value);
                      }
                    }}
                  >
                    <X size={12} />
                  </span>
                </Badge>
              );
            })}
          </div>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command>
          <CommandInput
            className={INPUT_PLACEHOLDER_CLASS}
            placeholder={t('search.sidebar.searchFilterValues', { label })}
          />
          <CommandList>
            <CommandEmpty>
              {t('search.sidebar.noFilterValueFound')}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  className="min-h-11 lg:min-h-9"
                  onSelect={() => {
                    onToggle(option.value);
                    if (single) setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4 shrink-0',
                      selected.includes(option.value)
                        ? 'opacity-100'
                        : 'opacity-0',
                    )}
                  />
                  <span className="truncate text-xs">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
